/**
 * The offering-document vault: a product's PPM, term sheet and supplements.
 *
 * This is not an ordinary admin screen. An unacknowledged PUBLISHED offering
 * document makes contract-case creation answer 403 — so until a document is
 * uploaded and published here, nobody can sign a contract for that product, and
 * the client-side read and acknowledgement (already built) have nothing to show.
 *
 * Upload is three steps on purpose: the browser PUTs straight to storage with a
 * presigned URL (the file never passes through the API), then the server
 * re-reads the object it just received and checks its size, its MIME type and
 * its SHA-256 before registering it. Publishing is a separate, reasoned act.
 */
import type { AxiosInstance } from 'axios';
import authService from './auth.service';

/** What a product's vault may hold. Mirrors the server's DOC_TYPES. */
export const DOC_TYPES = ['ppm', 'term_sheet', 'supplement'] as const;
export type DocType = (typeof DOC_TYPES)[number];

export const DOC_TYPE_LABEL: Record<DocType, string> = {
  ppm: 'Private placement memorandum',
  term_sheet: 'Term sheet',
  supplement: 'Supplement',
};

export interface OfferingDocument {
  document_id: string;
  doc_type: DocType;
  version: number;
  file_hash: string;
  file_size: number;
  is_published: boolean;
  published_at: string | null;
  created_at: string;
}

export interface PresignedUpload {
  upload_url: string;
  object_key: string;
  version: number;
  expires_in_seconds: number;
}

class OfferingDocumentsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async list(productId: string): Promise<OfferingDocument[]> {
    const res = await this.api.get<{ items: OfferingDocument[] }>(
      `/api/v1/staff/offering-documents/${productId}`,
    );
    return res.data.items;
  }

  async presignUpload(productId: string, docType: DocType): Promise<PresignedUpload> {
    const res = await this.api.post<PresignedUpload>(
      '/api/v1/staff/offering-documents/upload-presign',
      { product_id: productId, doc_type: docType },
    );
    return res.data;
  }

  /**
   * PUT the file straight to storage. Deliberately NOT through `this.api`: the
   * presigned URL carries its own authorization, and sending our bearer token
   * to a storage host would hand it to a third party. The Content-Type must be
   * exactly what the signature was made for, or the PUT is refused.
   */
  async uploadToStorage(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': 'application/pdf' },
    });
    if (!response.ok) {
      throw new Error(`The upload was refused by storage (${response.status}).`);
    }
  }

  /** The server re-reads what it received and checks it before registering. */
  async confirmUpload(
    productId: string,
    docType: DocType,
    objectKey: string,
  ): Promise<{ document_id: string; version: number }> {
    const res = await this.api.post<{ document_id: string; version: number }>(
      '/api/v1/staff/offering-documents/upload-confirm',
      { product_id: productId, doc_type: docType, object_key: objectKey },
    );
    return res.data;
  }

  /** Publishing is what makes a document binding on new contracts. */
  async publish(documentId: string, reason: string): Promise<void> {
    await this.api.post(`/api/v1/staff/offering-documents/${documentId}/publish`, { reason });
  }
}

export default new OfferingDocumentsService();
